import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { APP } from './tokens';

/**
 * Tabs available in the project-workspace top banner.
 *
 * Live tabs: `'documents'`, `'issues'` (labeled **Clause flags** in the UI).
 *
 * `'chat'`, `'reviews'`, `'tabular'` are listed in the union for
 * back-compat — older sessions persist these values, and the parent
 * coerces them to `'documents'` before rendering. Reasons each is
 * absent from the visible tab list:
 *   - `'chat'`    : was a redundant view-mode (full-width chat) that
 *                   duplicated the always-visible chat side panel.
 *                   Removed in favor of `'issues'` (see
 *                   docs/plans/2026-05-08-project-issues-tab-design.md).
 *   - `'reviews'` : ship-blocked placeholder; will return when the
 *                   reviews flow is real.
 *   - `'tabular'` : same — placeholder, parked.
 */
export type ProjectWorkspaceTab = 'documents' | 'issues' | 'chat' | 'reviews' | 'tabular';

const TAB_ROW: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  flexShrink: 0,
  padding: '8px 16px 10px',
  borderBottom: `1px solid ${APP.border}`,
  backgroundColor: APP.bgContent,
  fontFamily: APP.fontFamily,
};

const TAB_BTN_BASE: CSSProperties = {
  border: 'none',
  borderRadius: 8,
  padding: '6px 12px',
  fontSize: 12,
  fontWeight: 500,
  cursor: 'pointer',
  fontFamily: APP.fontFamily,
  backgroundColor: 'transparent',
  color: APP.textMuted,
};

export default function ProjectWorkspaceTabs({
  projectTitle,
  activeTab,
  onTabChange,
  onRenameProject,
  issuesCount,
  issuesHasUrgent,
}: {
  projectTitle: string;
  activeTab: ProjectWorkspaceTab;
  onTabChange: (tab: ProjectWorkspaceTab) => void;
  /**
   * Optional rename callback. When provided, the title becomes
   * inline-editable: click → input field → Enter / blur to commit,
   * Escape to cancel. The parent is responsible for persisting the
   * rename (e.g., calling `projectApi.renameProject`) and refreshing
   * the project so the new title flows back through props.
   */
  onRenameProject?: (nextTitle: string) => void;
  /**
   * Open clause-flag count for the Clause flags tab badge. When zero or
   * undefined the badge is hidden entirely.
   */
  issuesCount?: number;
  /**
   * Whether any open clause flag has severity `critical` or `high`.
   * Drives the badge color (red when true, neutral otherwise).
   */
  issuesHasUrgent?: boolean;
}) {
  const tabs: Array<{ id: ProjectWorkspaceTab; label: string }> = [
    { id: 'documents', label: 'Documents' },
    { id: 'issues',    label: 'Clause flags' },
  ];

  // ── Inline title edit ──────────────────────────────────────────
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(projectTitle);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Re-sync the draft whenever the project itself changes underneath us
  // (rename happened on another tab, project was switched, etc).
  useEffect(() => {
    if (!editing) setDraft(projectTitle);
  }, [projectTitle, editing]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const commit = () => {
    const next = draft.trim();
    setEditing(false);
    if (!next || next === projectTitle) {
      setDraft(projectTitle);
      return;
    }
    onRenameProject?.(next);
  };

  const cancel = () => {
    setDraft(projectTitle);
    setEditing(false);
  };

  const renameDisabled = !onRenameProject;

  return (
    <div style={TAB_ROW}>
      <span
        style={{
          fontSize: 11,
          fontWeight: 600,
          color: APP.textMuted,
          marginRight: 4,
          flexShrink: 0,
        }}
      >
        Project ·
      </span>
      {editing ? (
        <input
          ref={inputRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commit();
            } else if (e.key === 'Escape') {
              e.preventDefault();
              cancel();
            }
          }}
          onBlur={commit}
          maxLength={200}
          aria-label="Project title"
          style={{
            // Match the static label visually so the inline-edit
            // affordance feels native, not modal.
            border: `1px solid ${APP.cornerGradientStart}`,
            borderRadius: 5,
            padding: '2px 6px',
            backgroundColor: APP.bgContent,
            color: APP.textPrimary,
            fontFamily: APP.fontFamily,
            fontSize: 11,
            fontWeight: 600,
            outline: 'none',
            minWidth: 120,
            maxWidth: 280,
            marginRight: 8,
          }}
        />
      ) : (
        <button
          type="button"
          title={renameDisabled ? projectTitle : `${projectTitle} · click to rename`}
          onClick={() => {
            if (renameDisabled) return;
            setEditing(true);
          }}
          disabled={renameDisabled}
          style={{
            border: 'none',
            background: 'transparent',
            padding: '2px 4px',
            borderRadius: 4,
            fontSize: 11,
            fontWeight: 600,
            color: APP.textPrimary,
            fontFamily: APP.fontFamily,
            maxWidth: 280,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            marginRight: 8,
            cursor: renameDisabled ? 'default' : 'text',
          }}
          onMouseEnter={(e) => {
            if (renameDisabled) return;
            (e.currentTarget as HTMLButtonElement).style.backgroundColor =
              'var(--app-bg-history-active)';
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent';
          }}
        >
          {projectTitle}
        </button>
      )}
      <div style={{ width: 1, height: 16, backgroundColor: APP.border, marginRight: 8 }} />
      {tabs.map((t) => {
        const on = activeTab === t.id;
        const showIssuesBadge = t.id === 'issues' && typeof issuesCount === 'number' && issuesCount > 0;
        return (
          <button
            key={t.id}
            type="button"
            onClick={() => onTabChange(t.id)}
            style={{
              ...TAB_BTN_BASE,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              backgroundColor: on ? APP.bgSidebar : 'transparent',
              color: on ? APP.textPrimary : APP.textMuted,
              boxShadow: on ? '0 1px 2px rgba(26,23,20,0.06)' : 'none',
            }}
          >
            <span>{t.label}</span>
            {showIssuesBadge && (
              <span
                aria-label={`${issuesCount} open clause flag${issuesCount === 1 ? '' : 's'}`}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: 18,
                  height: 16,
                  padding: '0 5px',
                  borderRadius: 999,
                  fontSize: 10,
                  fontWeight: 700,
                  fontFamily: APP.fontFamily,
                  lineHeight: 1,
                  // Red when there's at least one critical/high flag,
                  // neutral muted otherwise. Keeps the count visible
                  // in both states without screaming when severity is
                  // mild.
                  backgroundColor: issuesHasUrgent ? '#C04A2A' : 'var(--app-bg-history-active)',
                  color: issuesHasUrgent ? '#FDFCFB' : APP.textSecondary,
                }}
              >
                {issuesCount}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
